// ScraperHistory.tsx - Scraper run history component
import { useState, useEffect } from 'react';
import {
  Paper,
  Stack,
  Group,
  Text,
  Title,
  Table,
  Badge,
  Select,
  Pagination,
  Button,
} from '@mantine/core';
import { IconRefresh, IconClock } from '@tabler/icons-react';
import { useScraperHistory } from '@/hooks/useScrapers';

interface RunHistory {
  id: string;
  source: string;
  status: string;
  started_at: number;
  completed_at: number | null;
  ads_found: number;
  ads_new: number;
  ads_modified: number;
  ads_removed: number;
  ads_processed: number;
  ads_failed: number;
  error: string | null;
}

export function ScraperHistory({ source: initialSource }: { source?: string }) {
  const [selectedSource, setSelectedSource] = useState<string>(initialSource || '');
  const [page, setPage] = useState(1);
  const limit = 20;
  
  const { data: history = [], isLoading: loading, refetch } = useScraperHistory(
    selectedSource || 'ss-com',
    limit
  );

  // Get available scraper sources
  const scraperSources = [
    { value: 'ss-com', label: 'SS.com' },
    { value: 'autoscout24', label: 'AutoScout24' },
    { value: 'mobile-de', label: 'Mobile.de' },
    { value: 'ebay-motors', label: 'eBay Motors' },
    { value: 'facebook', label: 'Facebook Marketplace' },
  ];

  useEffect(() => {
    if (initialSource && !selectedSource) {
      setSelectedSource(initialSource);
    }
  }, [initialSource, selectedSource]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'green';
      case 'failed':
        return 'red';
      case 'running':
        return 'blue';
      default:
        return 'gray';
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatDuration = (started: number, completed: number | null) => {
    if (!completed) return 'Running...';
    const seconds = Math.floor((completed - started) / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ${seconds % 60}s`;
  };

  if (loading) {
    return <div>Loading history...</div>;
  }

  return (
    <Paper p="md" radius="md" withBorder>
      <Stack gap="md">
        <Group justify="space-between">
          <div>
            <Title order={3}>Run History</Title>
            <Text size="sm" c="dimmed">
              Recent scraper execution history
            </Text>
          </div>
          <Group>
            <Select
              placeholder="Select scraper"
              data={scraperSources}
              value={selectedSource}
              onChange={(value) => setSelectedSource(value || '')}
              style={{ minWidth: 150 }}
            />
            <Button
              variant="subtle"
              leftSection={<IconRefresh size={16} />}
              onClick={() => refetch()}
              disabled={!selectedSource}
            >
              Refresh
            </Button>
          </Group>
        </Group>

        {!selectedSource && (
          <Text c="dimmed" ta="center" py="xl">
            Please select a scraper to view history
          </Text>
        )}

        {!selectedSource ? null : history.length === 0 ? (
          <Text c="dimmed" ta="center" py="xl">
            No run history available
          </Text>
        ) : (
          <>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Started</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Duration</Table.Th>
                  <Table.Th>Ads Found</Table.Th>
                  <Table.Th>New</Table.Th>
                  <Table.Th>Modified</Table.Th>
                  <Table.Th>Processed</Table.Th>
                  <Table.Th>Failed</Table.Th>
                  {selectedSource === 'ss-com' && (
                    <>
                      <Table.Th>Expires At</Table.Th>
                      <Table.Th>View Count</Table.Th>
                      <Table.Th>Archived</Table.Th>
                    </>
                  )}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {history.map((run: any) => (
                  <Table.Tr key={run.id}>
                    <Table.Td>
                      <Text size="sm">{formatDate(run.started_at)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge color={getStatusColor(run.status)} size="sm">
                        {run.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {formatDuration(run.started_at, run.completed_at)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={600}>
                        {run.ads_found}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" color="green" variant="light">
                        {run.ads_new}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" color="blue" variant="light">
                        {run.ads_modified}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{run.ads_processed || 0}</Text>
                    </Table.Td>
                    <Table.Td>
                      {run.ads_failed > 0 ? (
                        <Badge size="sm" color="red" variant="light">
                          {run.ads_failed}
                        </Badge>
                      ) : (
                        <Text size="sm">0</Text>
                      )}
                    </Table.Td>
                    {selectedSource === 'ss-com' && (
                      <>
                        <Table.Td>
                          <Text size="xs" c="dimmed">
                            {run.expires_at ? formatDate(run.expires_at) : 'N/A'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{run.view_count || 'N/A'}</Text>
                        </Table.Td>
                        <Table.Td>
                          {run.is_archived ? (
                            <Badge size="sm" color="orange" variant="light">
                              Yes
                            </Badge>
                          ) : (
                            <Badge size="sm" color="gray" variant="light">
                              No
                            </Badge>
                          )}
                        </Table.Td>
                      </>
                    )}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>

            {history.length >= limit && (
              <Group justify="center" mt="md">
                <Pagination
                  value={page}
                  onChange={setPage}
                  total={Math.ceil(history.length / limit)}
                />
              </Group>
            )}
          </>
        )}
      </Stack>
    </Paper>
  );
}
